import React, { useState, useEffect } from "react";
import { Form, Button, Container, Alert, Table, Badge } from "react-bootstrap";
import { useParams } from "react-router-dom";
import AdminSidebar from "../../../components/admin/sidebar/adminSidebar";
import axiosInstance from "../../../api/axios";

export default function AdminUserDetail() {
  const { userId } = useParams(); // URL에서 userId 추출
  const [userData, setUserData] = useState({
    memberType: "",
    userType: "",
    nameKor: "",
    nameEng: "",
    birthDate: "",
    email: "",
    organization: "",
    position: "",
    address: "",
    phone: "",
    educations: [],
    experiences: [],
    status: "", // 사용자 상태
  });
  const [selectedUserType, setSelectedUserType] = useState(""); // 선택된 계정 유형
  const [originalUserData, setOriginalUserData] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [updateLoading, setUpdateLoading] = useState(false); // 계정 유형 업데이트 로딩 상태
  const [statusLoading, setStatusLoading] = useState(false); // 승인/반려 로딩 상태

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await axiosInstance.get(`/api/user/detail/${userId}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        });
        setUserData(response.data.data);
        setOriginalUserData(response.data.data); // 초기값 저장
        setSelectedUserType(response.data.data.userType);
      } catch (err) {
        console.error("Failed to load user data:", err);
        setError("사용자 정보를 가져오는데 실패했습니다.");
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [userId]);

  const updateUserDetails = async () => {
    try {
      setUpdateLoading(true);

      const updatedData = {
        ...(userData.position !== originalUserData.position && {
          position: userData.position,
        }),
        ...(selectedUserType !== originalUserData.userType && {
          userType: selectedUserType,
        }),
      };

      if (Object.keys(updatedData).length === 0) {
        alert("변경 사항이 없습니다.");
        setUpdateLoading(false);
        return;
      }

      await axiosInstance.put(
        `/api/user/edit/${userId}`,
        updatedData, // 변경된 데이터만 전송
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );

      alert("사용자 정보가 성공적으로 업데이트되었습니다.");
    } catch (err) {
      console.error("Failed to update user details:", err);
      alert("사용자 정보 업데이트 중 문제가 발생했습니다.");
    } finally {
      setUpdateLoading(false);
    }
  };

  const updateStatus = async (newStatus) => {
    try {
      setStatusLoading(true);
      await axiosInstance.put(
        `/api/user/status/${userId}`,
        { status: newStatus },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );
      setUserData((prevData) => ({
        ...prevData,
        status: newStatus,
      }));
      alert(
        `사용자 상태가 ${newStatus === "APPROVED" ? "승인" : "반려"}되었습니다.`
      );
    } catch (error) {
      console.error("Failed to update status:", error);
      alert("상태 업데이트 중 문제가 발생했습니다.");
    } finally {
      setStatusLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserData((prev) => ({ ...prev, [name]: value }));
  };

  const getMemberTypeLabel = (memberType) => {
    switch (memberType) {
      case "REGULAR":
        return "정회원";
      case "ASSOCIATE":
        return "준회원";
      case "GROUP":
        return "단체회원";
      default:
        return "알 수 없음";
    }
  };

  if (loading) {
    return <p className="text-center">로딩 중...</p>;
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  return (
    <AdminSidebar>
      <Container className="my-5">
        <h2 className="text-center mb-4">사용자 상세 정보</h2>

        <Table bordered>
          <tbody>
            <tr>
              <td>계정유형</td>
              <td colSpan="3">
                <Form>
                  <Form.Check
                    inline
                    type="radio"
                    name="userType"
                    label="관리자"
                    value="ADMIN"
                    checked={selectedUserType === "ADMIN"}
                    onChange={(e) => setSelectedUserType(e.target.value)}
                  />
                  <Form.Check
                    inline
                    type="radio"
                    name="userType"
                    label="사용자"
                    value="USER"
                    checked={selectedUserType === "USER"}
                    onChange={(e) => setSelectedUserType(e.target.value)}
                  />
                </Form>
              </td>
            </tr>
            <tr>
              <td>회원구분</td>
              <td colSpan="3">{getMemberTypeLabel(userData.memberType)}</td>
            </tr>
            <tr>
              <td>성명</td>
              <td>{userData.nameKor}</td>
              <td>{userData.nameEng}</td>
            </tr>
            <tr>
              <td>생년월일</td>
              <td colSpan="3">{userData.birthDate}</td>
            </tr>
            <tr>
              <td>E-mail</td>
              <td colSpan="3">{userData.email}</td>
            </tr>
            <tr>
              <td>소속기관</td>
              <td>{userData.organization}</td>
              <td>
                <Form.Control
                  type="text"
                  name="position"
                  value={userData.position}
                  onChange={handleInputChange}
                />
              </td>
            </tr>
            <tr>
              <td>주소</td>
              <td colSpan="3">{userData.address}</td>
            </tr>
            <tr>
              <td>전화</td>
              <td colSpan="3">{userData.phone}</td>
            </tr>
            <tr>
              <td>상태</td>
              <td colSpan="3">
                <Badge
                  bg={
                    userData.status === "APPROVED"
                      ? "success"
                      : userData.status === "REJECTED"
                      ? "danger"
                      : "warning"
                  }
                >
                  {userData.status === "APPROVED"
                    ? "승인"
                    : userData.status === "REJECTED"
                    ? "반려"
                    : "대기"}
                </Badge>
              </td>
            </tr>
          </tbody>
        </Table>

        <h5>학력사항</h5>
        <Table bordered>
          <thead>
            <tr>
              <th>년</th>
              <th>월</th>
              <th>내용</th>
            </tr>
          </thead>
          <tbody>
            {userData.educations.map((edu, index) => (
              <tr key={index}>
                <td>{edu.year}</td>
                <td>{edu.month}</td>
                <td>{edu.details}</td>
              </tr>
            ))}
          </tbody>
        </Table>

        <h5>경력사항</h5>
        <Table bordered>
          <thead>
            <tr>
              <th>년</th>
              <th>월</th>
              <th>내용</th>
            </tr>
          </thead>
          <tbody>
            {userData.experiences.map((exp, index) => (
              <tr key={index}>
                <td>{exp.year}</td>
                <td>{exp.month}</td>
                <td>{exp.details}</td>
              </tr>
            ))}
          </tbody>
        </Table>

        <div className="text-center mt-4">
          <Button
            variant="success"
            className="me-2"
            onClick={() => updateStatus("APPROVED")}
            disabled={statusLoading}
          >
            {statusLoading ? "승인 중..." : "승인"}
          </Button>
          <Button
            variant="danger"
            onClick={() => updateStatus("REJECTED")}
            disabled={statusLoading}
          >
            {statusLoading ? "반려 중..." : "반려"}
          </Button>
        </div>

        <div className="text-center mt-4">
          <Button
            variant="primary"
            onClick={updateUserDetails}
            disabled={updateLoading}
          >
            {updateLoading ? "저장 중..." : "저장"}
          </Button>
        </div>

        <div className="text-center mt-4">
          <Button
            variant="secondary"
            onClick={() => window.history.back()}
            className="me-2"
          >
            뒤로가기
          </Button>
        </div>
      </Container>
    </AdminSidebar>
  );
}
